<script setup lang="ts">
import { type Invoice, type InvoiceStatus } from '@/api/invoice'
import { fileClaim, type FileClaimRequest } from '@/api/profiles'
import chevronDownIcon from '@/assets/icons/chevron-down.svg'
import chevronLeftIcon from '@/assets/icons/chevron-left.svg'
import plusIcon from '@/assets/icons/plus.svg'
import { useFileAClaimStore } from '@/stores/useClaimStore'
import { formatPrice } from '@/utils'
import { useMutation } from '@pinia/colada'
import { storeToRefs } from 'pinia'
import { useForm } from 'vee-validate'
import { useI18n } from 'vue-i18n'
import { pipe, minLength, string, object, array, any } from 'valibot'
import { toTypedSchema } from '@vee-validate/valibot'
definePage({
  beforeEnter(to, from, next) {
    const claimStore = useFileAClaimStore()
    if (!isDefined(claimStore.selectedInvoice)) {
      next('/claims-complaints/file-a-claim/select-invoice')
      return
    }
    next()
  },
})

const { t } = useI18n()
const claimStore = useFileAClaimStore()
const { selectedInvoice } = storeToRefs(claimStore) as {
  selectedInvoice: Ref<Invoice>
}
const showVisualDictionaryModal = ref(false)
const router = useRouter()
const statusClassMap: Record<InvoiceStatus, string> = {
  Paid: 'bg-glue',
  PartiallyPaid: 'bg-green',
  Unpaid: 'bg-red-light',
}

const { handleSubmit, values } = useForm<FileClaimRequest>({
  initialValues: {
    files: [],
    areas: [],
    description: '',
    invoiceNumber: selectedInvoice.value.number,
    itemKey: '',
  },
  validationSchema: toTypedSchema(
    object({
      files: any(),
      areas: pipe(
        array(pipe(string(), minLength(1, t('general.errorMessages.required')))),
        minLength(1, t('general.errorMessages.required')),
      ),
      description: pipe(string(), minLength(1, t('general.errorMessages.required'))),
      invoiceNumber: pipe(string(), minLength(1, t('general.errorMessages.required'))),
      itemKey: pipe(string(), minLength(1, t('general.errorMessages.required'))),
    }),
  ),
})

const { mutateAsync } = useMutation({
  mutation: fileClaim,
  onSuccess: () => {
    router.push('/claims-complaints/file-a-claim/complete')
  },
})

const submitForm = handleSubmit(async data => {
  await mutateAsync(data)
})
</script>

<template>
  <form @submit.prevent="submitForm">
    <RouterLink
      to="/claims-complaints/file-a-claim/select-invoice"
      class="inline-flex"
    >
      <BaseIcon
        class="inline-block h-6 w-6"
        :src="chevronLeftIcon"
      />
      <span class="mb-4 ml-2 text-base">
        {{ t('claimsComplaints.describeClaim.backButton') }}
      </span>
    </RouterLink>
    <h1 class="pb-4 text-3xl font-bold">
      {{ t('claimsComplaints.describeClaim.title') }}
    </h1>
    <DataTable
      :values="[selectedInvoice]"
      :header="t('invoices.InvoiceHistory.title')"
    >
      <template #default="{ value }">
        <DataTableColumn
          :header="t('invoices.InvoiceHistory.orderDate')"
          :mobile-order="4"
          highlight
        >
          {{ value?.invoiceDate }}
        </DataTableColumn>
        <DataTableColumn
          :header="t('invoices.invoiceNumber')"
          :mobile-order="1"
        >
          <span>{{ value?.number }}</span>
        </DataTableColumn>
        <DataTableColumn
          :header="t('invoices.InvoiceHistory.amount')"
          :mobile-order="4"
        >
          {{ formatPrice(value?.price) }}
        </DataTableColumn>
        <DataTableColumn
          :header="t('claimsComplaints.describeClaim.fileAclaim.status')"
          mobile-full-width
          mobile-header-hidden
          :mobile-order="2"
        >
          <Status
            :status="(value?.status as InvoiceStatus) ?? ''"
            :status-options="statusClassMap"
          />
        </DataTableColumn>
      </template>
    </DataTable>
    <div class="space-y-5 md:flex md:space-x-8">
      <div class="space-y-5 md:w-1/2">
        <Frame>
          <InputField
            name="itemKey"
            type="text"
            class="bg-gray-50 text-sm font-normal text-gray-900 italic shadow-xs"
            :placeholder="t('claimsComplaints.describeClaim.selectItem.placeholder')"
          />
        </Frame>
        <Frame>
          <h3 class="mb-4 text-2xl font-semibold">
            {{ t('claimsComplaints.describeClaim.accordion.title') }}
          </h3>
          <CheckboxAccordion
            name="areas"
            :title="t('claimsComplaints.describeClaim.accordion.finishingAndWrapping')"
            :options="[
              t('claimsComplaints.describeClaim.accordion.wrapping'),
              t('claimsComplaints.describeClaim.accordion.strapping'),
              t('claimsComplaints.describeClaim.accordion.palletizing'),
              t('claimsComplaints.describeClaim.accordion.bottomFoil'),
            ]"
          />

          <CheckboxAccordion
            name="areas"
            :title="t('claimsComplaints.describeClaim.accordion.labelling')"
            :options="[
              t('claimsComplaints.describeClaim.accordion.badAfterpressing'),
              t('claimsComplaints.describeClaim.accordion.badFoldingLine'),
              t('claimsComplaints.describeClaim.accordion.beard'),
              t('claimsComplaints.describeClaim.accordion.closingProblems'),
              t('claimsComplaints.describeClaim.accordion.colour'),
              t('claimsComplaints.describeClaim.accordion.denestingProblems'),
              t('claimsComplaints.describeClaim.accordion.holesInProduct'),
              t('claimsComplaints.describeClaim.accordion.lackOfRigidity'),
              t('claimsComplaints.describeClaim.accordion.sportInFibre'),
            ]"
          />

          <CheckboxAccordion
            name="areas"
            :title="t('claimsComplaints.describeClaim.accordion.printing')"
            :options="[
              t('claimsComplaints.describeClaim.accordion.looseOrWronglyPlacedLabel'),
              t('claimsComplaints.describeClaim.accordion.missingLabel'),
              t('claimsComplaints.describeClaim.accordion.tooMuchGlue'),
              t('claimsComplaints.describeClaim.accordion.errorOnLabel'),
              t('claimsComplaints.describeClaim.accordion.colourDeviation'),
              t('claimsComplaints.describeClaim.accordion.mixedLablesOnPallet'),
              t('claimsComplaints.describeClaim.accordion.barcodeReadability'),
              t('claimsComplaints.describeClaim.accordion.doublePrint'),
              t('claimsComplaints.describeClaim.accordion.missingPrint'),
              t('claimsComplaints.describeClaim.accordion.smudgedCliché'),
              t('claimsComplaints.describeClaim.accordion.vaguePrint'),
              t('claimsComplaints.describeClaim.accordion.wetInk'),
              t('claimsComplaints.describeClaim.accordion.wrongCluché'),
              t('claimsComplaints.describeClaim.accordion.tooFewProductsInBundles'),
            ]"
          />

          <CheckboxAccordion
            name="areas"
            :title="t('claimsComplaints.describeClaim.accordion.logistics')"
            :options="[
              t('claimsComplaints.describeClaim.accordion.customsProblems'),
              t('claimsComplaints.describeClaim.accordion.deliveredAtWrongAddress'),
              t('claimsComplaints.describeClaim.accordion.earlyDelivery'),
              t('claimsComplaints.describeClaim.accordion.lateDelivery'),
              t('claimsComplaints.describeClaim.accordion.tooFewProductsDelivered'),
              t('claimsComplaints.describeClaim.accordion.wrongGoodsDelivered'),
            ]"
          />

          <CheckboxAccordion
            name="areas"
            :title="t('claimsComplaints.describeClaim.accordion.transport')"
            :options="[
              t('claimsComplaints.describeClaim.accordion.badEuroPallets'),
              t('claimsComplaints.describeClaim.accordion.destroyedGoods'),
              t('claimsComplaints.describeClaim.accordion.dirtyItems'),
              t('claimsComplaints.describeClaim.accordion.wetItems'),
            ]"
          />
          <ErrorMessage name="areas" />
          <div>
            <p class="my-8 text-base font-normal">
              {{ t('claimsComplaints.describeClaim.modal.guideInfo') }}
            </p>
            <button
              class="flex text-base font-normal underline"
              @click="showVisualDictionaryModal = true"
            >
              {{ t('claimsComplaints.describeClaim.modal.button') }}
              <BaseIcon
                class="ml-2 block h-6 w-6"
                :src="chevronDownIcon"
              />
            </button>
            <Modal v-model:visible="showVisualDictionaryModal">
              <VisualDictionaryModal />
            </Modal>
          </div>
        </Frame>
        <Frame>
          <h3 class="mb-4 text-2xl font-semibold">
            {{ t('claimsComplaints.describeClaim.textInputBox.title') }}
          </h3>
          <TextField
            name="description"
            label="Description"
            :placeholder="t('claimsComplaints.describeClaim.textInputBox.placeholder')"
          />
        </Frame>
        <Frame>
          <h3 class="mb-4 text-2xl font-semibold">
            {{ t('claimsComplaints.describeClaim.fileUpload.title') }}
          </h3>
          <div class="mb-8 flex flex-col space-y-5">
            <p class="flex items-center text-base font-normal">
              <BaseIcon
                class="mr-2 block h-5 w-5"
                :src="plusIcon"
              />

              {{ t('claimsComplaints.describeClaim.fileUpload.pictureLabel') }}
            </p>
            <p class="flex items-center text-base font-normal">
              <BaseIcon
                class="mr-2 block h-5 w-5"
                :src="plusIcon"
              />

              {{ t('claimsComplaints.describeClaim.fileUpload.pictureProduct') }}
            </p>
          </div>
          <FileUpload name="files" />
        </Frame>
      </div>
      <Frame class="order-5 p-10 md:order-none md:w-1/2 md:self-start md:justify-self-end">
        <h3 class="mb-4 text-base font-semibold">
          {{ t('claimsComplaints.describeClaim.summary.title') }}
        </h3>

        <Summary
          :files="values.files"
          :description="values.description"
          :areas="values.areas"
          :order-number="selectedInvoice?.number ?? ''"
        />
        <Button
          color="green"
          type="submit"
          class="mt-4"
        >
          {{ t('claimsComplaints.describeClaim.summary.button') }}
        </Button>
      </Frame>
    </div>
  </form>
</template>
